import React from 'react';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { Box, Chip, CircularProgress, ToggleButtonGroup, Typography } from '@mui/material';
import IRRDataPoint from '../../../models/IRRDataPoint';
import IRRBarChart from '../../../components/IRRBarChart';
import IRRLineChart from '../../../components/IRRLineChart';
import MultiIRRBarChart from '../../../components/MultiIRRBarChart';
import MultiIRRLineChart from '../../../components/MultiIRRLineChart';
import {
  StyledButtonGroup,
  StyledToggleButton,
  StyledToggleButtonGroup,
} from '../../../components/styles/StyledButtons';
import type { MultiChartPoint } from './contactPortfolioChartUtils';
import ContactPortfolioSectionCard from './ContactPortfolioSectionCard';
import type { FundBreakdownEntry } from './contactPortfolioTypes';

// Wrapped in a plain MUI ToggleButtonGroup (see below), not
// StyledToggleButtonGroup — that wrapper's own "segmented" styles set
// `width: 100%` + `flex: 1 1 auto` on each button at the lg breakpoint, which
// fought any width set here for the same CSS property: sometimes losing
// (both buttons shrink to a sliver once the row is tight — confirmed via
// DevTools, a 152px flex-basis collapsing to 68px), sometimes "winning" in a
// way that still left `width: 100%` driving the flex-basis, so the button
// wouldn't shrink AND claimed the full row, overflowing past its panel.
// ContentEditorToolbar's View toggle never had this problem for the same
// reason: it also skips StyledToggleButtonGroup.
const DATA_TYPE_TOGGLE_WIDTH_SX = { width: '10rem', justifyContent: 'center' };

export default function ContactPortfolioPerformanceSection({
  loading,
  hasChartData,
  breakdown,
  selectedFundIds,
  dataType,
  combinedView,
  combinedData,
  multiData,
  onDataTypeChange,
  onCombinedViewChange,
  onToggleFund,
}: {
  loading: boolean;
  hasChartData: boolean;
  breakdown: FundBreakdownEntry[];
  selectedFundIds: Set<string>;
  dataType: 'cumulative' | 'discrete';
  combinedView: boolean;
  combinedData: IRRDataPoint[];
  multiData: { data: MultiChartPoint[]; keys: string[] };
  onDataTypeChange: (value: 'cumulative' | 'discrete') => void;
  onCombinedViewChange: (combined: boolean) => void;
  onToggleFund: (fundExternalId: string) => void;
}) {
  return (
    <ContactPortfolioSectionCard
      title="Portfolio Performance"
      icon={<ShowChartIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
      loading={loading}
    >
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && !hasChartData && (
        <Typography variant="body2" color="text.secondary">
          No cashflow data available yet.
        </Typography>
      )}

      {!loading && hasChartData && (
        <>
          <StyledButtonGroup
            justifyContent="flex-start"
            sx={{ mt: 0, mb: 2, flexWrap: 'wrap', gap: 1 }}
          >
            {breakdown.length > 1 && (
              <StyledToggleButtonGroup
                size="small"
                exclusive
                segmented={true}
                value={combinedView ? 'combined' : 'per_investment'}
                onChange={(_, val) => {
                  if (val) onCombinedViewChange(val === 'combined');
                }}
              >
                <StyledToggleButton value="combined">Combined</StyledToggleButton>
                <StyledToggleButton value="per_investment">Per Investment</StyledToggleButton>
              </StyledToggleButtonGroup>
            )}
            <ToggleButtonGroup
              size="small"
              exclusive
              value={dataType}
              onChange={(_, val) => {
                if (val) onDataTypeChange(val);
              }}
            >
              <StyledToggleButton value="cumulative" sx={DATA_TYPE_TOGGLE_WIDTH_SX}>
                Cumulative
              </StyledToggleButton>
              <StyledToggleButton value="discrete" sx={DATA_TYPE_TOGGLE_WIDTH_SX}>
                Discrete
              </StyledToggleButton>
            </ToggleButtonGroup>
          </StyledButtonGroup>

          {breakdown.length > 1 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
              {breakdown.map((fund) => {
                const selected = selectedFundIds.has(fund.fundExternalId);
                return (
                  <Chip
                    key={fund.fundExternalId}
                    label={fund.fundName}
                    size="small"
                    variant={selected ? 'filled' : 'outlined'}
                    color={selected ? 'success' : 'default'}
                    onClick={() => onToggleFund(fund.fundExternalId)}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          )}

          {combinedView || breakdown.length === 1 ? (
            dataType === 'cumulative' ? (
              <IRRLineChart data={combinedData} tone="tone1" />
            ) : (
              <IRRBarChart data={combinedData} tone="tone1" />
            )
          ) : dataType === 'cumulative' ? (
            <MultiIRRLineChart data={multiData.data} seriesKeys={multiData.keys} tone="tone1" />
          ) : (
            <MultiIRRBarChart data={multiData.data} seriesKeys={multiData.keys} tone="tone1" />
          )}
        </>
      )}
    </ContactPortfolioSectionCard>
  );
}
